import { useEffect, useMemo, useRef, useState } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { ShellColorTheme } from '@frontend/shell-theme';
import { shell } from '@frontend/shell-utils';
import { useThemeValues } from '@frontend/design-system';
import { staticCss } from './styles';
import { ColorDependentStyles, Viewport } from './types';

export const useConditionalShellTheme = () => {
  const isXSmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
  const { heightOffset } = useThemeValues();
  const { isShell, featureAvailability } = shell;

  const hasShellThemeEnabled = isShell && featureAvailability?.has('shell-theme');
  const showGlobalSearchOnTopBar = isXSmall;

  return {
    hasShellThemeEnabled: showGlobalSearchOnTopBar ? false : !!hasShellThemeEnabled,
    heightOffset: showGlobalSearchOnTopBar ? heightOffset : 0,
  };
};

export const useViewport = (): Viewport => {
  const [viewport, setViewport] = useState<Viewport>(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  }));

  const viewportRef = useRef(viewport);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let rafId: number;
    const handleResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const w = window.innerWidth;
        const h = window.innerHeight;

        if (w !== viewportRef.current.width || h !== viewportRef.current.height) {
          const next = { width: w, height: h };
          viewportRef.current = next;
          setViewport(next);
        }
      });
    };

    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return viewport;
};

export function useColorDependentStyles(colorObject: ShellColorTheme, isWindows: boolean): ColorDependentStyles {
  return useMemo(() => {
    const searchFieldContainer = css`
      ${staticCss.searchFieldContainerDefault};
      label {
        color: ${colorObject.iconColor};
      }
    `;

    const searchFieldContainerOpen = css`
      ${staticCss.searchFieldContainerDefaultOpen};
      label {
        color: ${colorObject.iconColor};
      }
    `;

    const shellBtnBase = [
      staticCss.shellBtnBaseStatic,
      css`
        color: ${colorObject.iconColor};
        svg {
          fill: ${colorObject.iconColor};
          color: ${colorObject.iconColor};
        }
        :hover {
          background: ${colorObject.hover} !important;
        }
      `,
    ];

    const shellBtnClosed: SerializedStyles[] = isWindows
      ? [...shellBtnBase, staticCss.windowsBtnCss]
      : [...shellBtnBase];

    const shellBtnOpen: SerializedStyles[] = [
      ...shellBtnBase,
      ...(isWindows ? [staticCss.windowsBtnCss] : []),
      css`
        background: ${colorObject.hover} !important;
      `,
    ];

    const shellSearchField = css`
      ${staticCss.searchFieldShell};
      button {
        &:focus {
          background: ${colorObject.helpColor};
        }
      }
    `;

    const shellSearchFieldContainer = css`
      ${staticCss.shellSearchFieldContainer};
      & input::placeholder {
        color: ${colorObject.iconColor} !important;
      }
      label {
        color: ${colorObject.iconColor};
      }
      button {
        :hover {
          background: ${colorObject.hover} !important;
        }
      }
      svg {
        color: ${colorObject.iconColor};
      }
    `;

    const shellSearchFieldContainerOpen = css`
      ${staticCss.shellSearchFieldContainerOpen};
      & input::placeholder {
        color: ${colorObject.iconColor} !important;
      }
      label {
        color: ${colorObject.iconColor};
      }
      button {
        :hover {
          background: ${colorObject.hover} !important;
        }
      }
      svg {
        color: ${colorObject.iconColor};
      }
    `;

    const dialogSizing = css`
      width: var(--gs-dialog-width);
      min-width: var(--gs-dialog-min-width);
    `;

    const dialogSearchShellWithMargin = css`
      ${staticCss.dialogSearchShellBase};
      margin-top: 10px;
    `;

    return {
      searchFieldContainer,
      searchFieldContainerOpen,
      shellSearchField,
      shellSearchFieldContainer,
      shellSearchFieldContainerOpen,
      shellBtnClosed,
      shellBtnOpen,
      dialogSizing,
      dialogSearchShellWithMargin,
    };
  }, [colorObject.iconColor, colorObject.hover, colorObject.helpColor, isWindows]);
}
